import { typographyToCss } from 'shared/lib/mui';
import styled, { css, DefaultTheme } from 'styled-components';

import { Sizes } from './types';

const getTypography = (theme: DefaultTheme, size: Sizes) => {
  if (size === Sizes.Small) {
    return typographyToCss(theme.typography.button);
  }

  if (size === Sizes.Large) {
    return typographyToCss(theme.typography.body1);
  }

  return typographyToCss(theme.typography.body2);
};

const getPadding = (theme: DefaultTheme, size: Sizes) => {
  if (size === Sizes.Small) {
    return `${theme.spacing(0.75)} ${theme.spacing(1)}`;
  }

  if (size === Sizes.Large) {
    return `${theme.spacing(2)} ${theme.spacing(2.5)}`;
  }

  return `${theme.spacing(1)} ${theme.spacing(2)}`;
};

const getMobilePadding = (theme: DefaultTheme, size: Sizes) => {
  if (size === Sizes.Small) {
    return `${theme.spacing(2)} ${theme.spacing(2)}`;
  }

  if (size === Sizes.Large) {
    return `${theme.spacing(3)} ${theme.spacing(3.5)}`;
  }

  return `${theme.spacing(2.5)} ${theme.spacing(3)}`;
};

const getAffixSpace = (size: Sizes) => {
  if (size === Sizes.Small) {
    return 5;
  }

  if (size === Sizes.Large) {
    return 8;
  }

  return 7;
};

const getAffixOffset = (size: Sizes) => {
  if (size === Sizes.Small) {
    return 1.5;
  }

  if (size === Sizes.Large) {
    return 2.5;
  }

  return 2;
};

const getAffixFontSize = (size: Sizes) => {
  if (size === Sizes.Small) {
    return '1rem';
  }

  if (size === Sizes.Large) {
    return '1.4rem';
  }

  return '1.2rem';
};

export const Container = styled.span`
  display: flex;
  align-items: center;
`;

export const Label = styled.label<{ $disabled: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.45;
    `};
`;

export const InputContainer = styled.span`
  position: relative;
  display: inline-block;
  width: 100%;
`;

export const Input = styled.input<{
  $hasError: boolean;
  $hasLeft: boolean;
  $hasRight: boolean;
  $transparent: boolean;
  $size: Sizes;
  $hasNumberButtons: boolean;
  $postFixWidth: number;
}>`
  ${({ theme, $size }) => getTypography(theme, $size)};

  margin: 0;
  padding: ${({ theme, $size }) => getPadding(theme, $size)};
  width: 100%;
  field-sizing: content;

  background: ${({ theme, $transparent }) =>
    $transparent ? 'transparent' : theme.palette.background.default};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  transition: border-color 200ms;

  color: inherit;

  &::-webkit-input-placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &::-moz-placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &:-ms-input-placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &[type='number'] {
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;

    text-align: right;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
    }
  }

  &[inputMode='numeric'] {
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;

    text-align: right;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
    }
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.45;
  }

  ${({ theme, $hasNumberButtons, $size }) =>
    $hasNumberButtons &&
    css`
      padding-right: ${theme.spacing(getAffixSpace($size))};
    `};

  ${({ theme, $hasError }) =>
    $hasError &&
    css`
      border-color: ${theme.palette.error.main};
    `};

  ${({ theme, $size }) =>
    theme.isMobile &&
    css`
      padding: ${getMobilePadding(theme, $size)};
    `};

  ${({ $hasLeft, $size, theme }) =>
    $hasLeft &&
    css`
      padding-left: ${theme.spacing(getAffixSpace($size))};
    `};

  ${({ $hasRight, $size, theme, $postFixWidth }) =>
    $hasRight &&
    css`
      padding-right: ${`calc(${$postFixWidth ?? 0}px + ${theme.spacing(getAffixSpace($size))})`};
    `};
`;

export const StartContainer = styled.span<{ $size: Sizes }>`
  position: absolute;
  top: 50%;
  height: 100%;
  left: ${({ theme, $size }) => theme.spacing(getAffixOffset($size))};

  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
  align-items: center;

  transform: translateY(-50%);
`;
export const EndContainer = styled.span<{ $size: Sizes }>`
  position: absolute;
  top: 50%;
  height: 100%;
  right: ${({ theme, $size }) => theme.spacing(getAffixOffset($size))};

  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
  align-items: center;

  transform: translateY(-50%);
`;

export const PostFix = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  pointer-events: none;

  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const AffixIconContainer = styled.span<{ $color: string | undefined }>`
  pointer-events: none;

  line-height: 62.5%;

  ${({ $color }) =>
    $color &&
    css`
      color: ${$color};
    `};
`;

export const NumberButtonsContainer = styled.span`
  display: flex;
  flex-direction: column;

  padding: ${({ theme }) => theme.spacing(0.5)};
`;

const ButtonBase = css<{ $size: Sizes }>`
  margin: 0;
  padding: 0;

  background: transparent;
  border: none;
  cursor: pointer;

  font-size: ${({ $size }) => getAffixFontSize($size)};
  line-height: 62.5%;
`;

export const NumberButton = styled.button<{
  $isSecond?: boolean;
  $size: Sizes;
}>`
  ${ButtonBase};

  display: flex;
  align-items: center;
  justify-content: center;

  transform: rotate(${({ $isSecond }) => ($isSecond ? 270 : 90)}deg);

  color: inherit;
`;

export const ClearButton = styled.button<{ $size: Sizes }>`
  ${ButtonBase};

  margin: 0;
  padding: 0;

  border: none;
  background: transparent;

  color: ${({ theme }) => theme.palette.text.secondary};
`;
